import {RockBottomGameAssets} from "../RockBottomGameAssets";
import {RockBottom} from "../RockBottom";
import {Layers, RockBottomLayers} from "./RockBottomLayers";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {RockBottomParsedGameData} from "../server/data/RockBottomParsedGameData";
import {RockBottomParsedInitData} from "../server/data/RockBottomParsedInitData";
import {RockBottomGameMode} from "../server/RockBottomGameMode";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";

export class RockBottomBackground extends PIXI.Container {

    private _gameBg: PIXI.Sprite;
    private _gameLogo: PIXI.Sprite;
    private _mainGameBgSpine: PIXI.spine.Spine;
    private _bonusGameBgSpine: PIXI.spine.Spine;
    private _boardFlickerSpine: PIXI.spine.Spine;
    private _layer: StageLayer | undefined;
    private _mainGameBgTexture: PIXI.Texture;
    private _bonusGameBgTexture: PIXI.Texture;
    private _overlay: PIXI.Graphics;

    constructor() {
        super();
        this.init();
        this.onOrientationChanged();
        this.onResize();
    }

    public onOrientationChanged() {
        const initData: RockBottomParsedInitData = RockBottom.model.parsedInitData;
        if (initData.isRestoreState && (initData.mode === RockBottomGameMode.FREESPIN || initData.mode === RockBottomGameMode.SUPER_FREESPIN)) {
            this._gameBg.texture = this._bonusGameBgTexture;
        }
    }

    public onResize() {
        this.setOverlayProps();
        const viewW: number = NolimitApplication.screenBounds.width,
            viewH: number = NolimitApplication.screenBounds.height,
            logoW: number = this._gameLogo.width,
            logoH: number = this._gameLogo.height,
            reelFrameW: number = 838,
            reelFrameH: number = 710;

        if (NolimitApplication.isLandscape) {
            const spaceLeft: number = ((viewW - reelFrameW) / 2);
            this._gameLogo.visible = spaceLeft > logoW;
            this._gameLogo.position.set(-(spaceLeft), (logoH / 2) + 10); // extra padding from top
        } else {
            const spaceTop: number = ((viewH - reelFrameH) / 2);
            this._gameLogo.visible = spaceTop > logoH;
            this._gameLogo.position.set(60, -((spaceTop - (logoH / 2)) / 2));
        }
    }

    public updateBackGround(mode: string) {
        if (mode === RockBottomGameMode.FREESPIN || mode === RockBottomGameMode.SUPER_FREESPIN) {
            this._gameBg.texture = this._bonusGameBgTexture;
            this.playBonusGameBGAnimations();
        } else {
            this._gameBg.texture = this._mainGameBgTexture;
            this.playMainGameBGAnimations();
        }
    }

    public stopBoardFlickering(): void {
        this.resetSpine(this._boardFlickerSpine);
    }

    public onSpinStart(): void {
        const parsedData: RockBottomParsedGameData | RockBottomParsedInitData = RockBottom.getParsedData();
        if (parsedData.nextMode === RockBottomGameMode.NORMAL) {
            this._boardFlickerSpine.visible = true;
            this._boardFlickerSpine.state.setAnimation(0, "Spin_Mode", true);
        }
        this.pauseBgIdleAnim();
    }

    public pauseBgIdleAnim(): void {
        this._mainGameBgSpine.state.timeScale = 0;
    }

    public resumeBgIdleAnim(): void {
        this._mainGameBgSpine.state.timeScale = 1;
    }

    public updateLevelUpTint(show: boolean): void {
        const symIdx: number = 4 - RockBottom.midSymExpand.getActiveSymbolId()!;
        this._overlay.visible = show;
        this._overlay.alpha = show ? (symIdx * 0.1) : 0;

        const blur: PIXI.filters.BlurFilter = new PIXI.filters.BlurFilter(),
            speedIdx: number = show ? (symIdx + 1) : 1;
        blur.blurX = show ? symIdx : 0;
        blur.blurY = show ? symIdx : 0;
        this._gameBg.filters = [blur];
        this._bonusGameBgSpine.filters = [blur];
        this._bonusGameBgSpine.state.timeScale = speedIdx;
    }

    private playMainGameBGAnimations(): void {
        const parsedData: RockBottomParsedGameData | RockBottomParsedInitData = RockBottom.getParsedData();
        if (parsedData.nextMode === RockBottomGameMode.NORMAL) {
            this.resetSpine(this._mainGameBgSpine);
            this.resetSpine(this._bonusGameBgSpine);
            this._mainGameBgSpine.visible = true;
            this._mainGameBgSpine.state.setAnimation(0, "Idle", true);
            this._mainGameBgSpine.state.timeScale = 1;
        }
    }

    private playBonusGameBGAnimations(): void {
        const parsedData: RockBottomParsedGameData | RockBottomParsedInitData = RockBottom.getParsedData();
        if (parsedData.nextMode === RockBottomGameMode.FREESPIN || parsedData.nextMode === RockBottomGameMode.SUPER_FREESPIN) {
            this.resetSpine(this._mainGameBgSpine);
            this.resetSpine(this._bonusGameBgSpine);
            this._bonusGameBgSpine.visible = true;
            this._bonusGameBgSpine.state.setAnimation(0, "Idle", true);
        }
    }

    private init() {
        this._layer = RockBottomLayers.getLayer(Layers.BACKGROUND);
        this.setBackgroundTextures();
        this.createBg();
        this.createMainGameIdleAnim();
        this.createBoardFlickering();
        this.createBonusGameIdleAnim();
        this.createLogo();
        this.createOverlay();
        this._layer?.addChild(this);
    }

    private setBackgroundTextures(): void {
        this._mainGameBgTexture = RockBottom.assetLoader.getTextures(RockBottomGameAssets.MAIN_GAME_BACKGROUND)[0];
        this._bonusGameBgTexture = RockBottom.assetLoader.getTextures(RockBottomGameAssets.BONUS_GAME_BACKGROUND)[0];
    }

    private createBg(): void {
        this._gameBg = new PIXI.Sprite(this._mainGameBgTexture);
        this._gameBg.position.set(360, 360);
        this._gameBg.anchor.set(0.5, 0.5);
        this.addChild(this._gameBg);
    }

    private createLogo(): void {
        this._gameLogo = new PIXI.Sprite(RockBottom.assetLoader.getTextures("logo")[0]);
        this._gameLogo.anchor.set(0, 0.5);
        this.addChild(this._gameLogo);
    }

    private createMainGameIdleAnim(): void {
        this._mainGameBgSpine = this.createSpine("MG_BG_Spine");
        this.addChild(this._mainGameBgSpine);
    }

    private createBoardFlickering(): void {
        this._boardFlickerSpine = this.createSpine("MG_BG_Light_Flicker_Spine");
        this.addChild(this._boardFlickerSpine);
    }

    private createBonusGameIdleAnim(): void {
        this._bonusGameBgSpine = this.createSpine("FS_BG_Spine");
        this.addChild(this._bonusGameBgSpine);
    }

    private createSpine(name: string): PIXI.spine.Spine {
        const spineObj: PIXI.spine.Spine = new PIXI.spine.Spine(RockBottom.assetLoader.getSpineAsset(name));
        spineObj.position.set(360);
        spineObj.visible = false;
        spineObj.name = name;
        return spineObj;
    }

    private resetSpine(currSpine: PIXI.spine.Spine): void {
        currSpine.skeleton.setToSetupPose();
        currSpine.updateTransform();
        currSpine.visible = false;
    }

    private createOverlay(): void {
        this._overlay = new PIXI.Graphics();
        this._overlay.visible = false;
        this._overlay.alpha = 0;
        this.setOverlayProps();
        this.addChild(this._overlay);
    }

    private setOverlayProps(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        this._overlay.clear();
        this._overlay.beginFill(0x000000, 1);
        this._overlay.drawRect(bounds.left, bounds.top, bounds.width, bounds.height);
        this._overlay.endFill();
    }
}